import { Autocomplete, Button, Flex, Grid, Label } from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { listCompanies } from "graphql/queries";

const ManagedCompanyForm = ({ onSubmit, type = "create", initialValues }) => {
  const [managedCompanyForm, setManagedCompanyForm] = useState(initialValues);
  const [companies, setCompanies] = useState({
    list: [],
    loading: false,
  });
  const [errors, setErrors] = useState({
    companyID: "",
  });
  const validateErrors = useErrors({
    companyID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(managedCompanyForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (checkIncorrectValue("companyID", companies.list)) return;

    if (!hasErrors) onSubmit({ ...managedCompanyForm });
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === managedCompanyForm[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;

    fetchCompanies({ name: value });
    setManagedCompanyForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setManagedCompanyForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    fetchCompanies({ name: value });
    setManagedCompanyForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const resetFormValues = () => {
    setErrors({
      companyID: "",
    });
    setManagedCompanyForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
  };

  async function fetchCompanies({ id = "", name = "" }) {
    try {
      setCompanies({ list: [], loading: true });
      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit: !id && !name ? 20 : undefined,
          filter: {
            and: [
              {
                name: { contains: name },
              },
            ],
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setManagedCompanyForm(initialValues);
    fetchCompanies({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr" }}>
          <Flex direction="column" gap="8px">
            <Label htmlFor="companyID">
              <LabelRequired text={strings.fields.name.label} />
            </Label>
            <Autocomplete
              id="companyID"
              name="companyID"
              isRequired
              placeholder={strings.fields.name.placeholder}
              value={
                companies.list.find(
                  (companyRec) => companyRec.id === managedCompanyForm.companyID
                )?.name ?? managedCompanyForm.companyID
              }
              options={companies.list.map((companyRec) => ({
                id: companyRec.id,
                label: companyRec.name,
              }))}
              isLoading={companies.loading}
              onClear={() => onClearSelect("companyID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("companyID", id)}
              errorMessage={errors.companyID}
              hasError={!!errors.companyID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
        </Grid>
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
    },
  },
});

export default ManagedCompanyForm;
